import { ComponentType } from 'react';

import {
  ExternalRouteRef,
  RouteRef,
  SubRouteRef,
} from '@backstage/core-plugin-api';

import {
  ItemRenderExtensionOptions,
  createItemRenderExtension,
  createItemRenderExtensions,
  useItemRenderExtensions,
} from '@spotify/confidence-core-react';
import { CollectionEntryFragment } from '@spotify/confidence-graphql';

export interface CollectionEntryOptions {
  entry: CollectionEntryFragment;
  resource: NonNullable<CollectionEntryFragment['resource']>;
}

export interface CollectionEntryExtensionOptions
  extends ItemRenderExtensionOptions<CollectionEntryOptions> {
  resourceType: string;
  routeRef?: RouteRef | SubRouteRef | ExternalRouteRef;
  getRouteParams?: (resourceName: string) => Record<string, string>;
  getDisplayName?: (resource: CollectionEntryOptions['resource']) => string;
  ResourceTypeLabel?: ComponentType<{
    resource: CollectionEntryOptions['resource'];
  }>;
}

export const CollectionEntryExtensions =
  createItemRenderExtensions<CollectionEntryExtensionOptions>({
    id: 'collection-entry',
  });

export const useCollectionEntryExtensions =
  (): CollectionEntryExtensionOptions[] => {
    return useItemRenderExtensions<
      ItemRenderExtensionOptions<CollectionEntryOptions>,
      CollectionEntryExtensionOptions
    >(CollectionEntryExtensions);
  };

export const createCollectionEntryExtension = (
  options: CollectionEntryExtensionOptions,
) => {
  return createItemRenderExtension<CollectionEntryExtensionOptions>(options);
};

export const findCollectionEntryExtension = (
  extensions: CollectionEntryExtensionOptions[],
  entry: CollectionEntryFragment,
): CollectionEntryExtensionOptions | undefined => {
  const resource = entry.resource;
  if (!resource || !('__typename' in resource)) {
    return undefined;
  }

  return extensions.find(ext =>
    ext.predicate({
      entry,
      resource: resource as CollectionEntryOptions['resource'],
    }),
  );
};

export const getExtensionDisplayName = (
  extension: CollectionEntryExtensionOptions | undefined,
  resource: CollectionEntryOptions['resource'],
  entry: CollectionEntryFragment,
): string => {
  if (extension?.getDisplayName) {
    return extension.getDisplayName(resource);
  }
  const r = resource as { displayName?: string; name?: string };
  return r.displayName ?? r.name ?? entry.name;
};
